import { useEffect, useState } from 'react';
import { safeParse } from '../lib/util.js';

/* ------------------------------------------------------------------------ */
/* localStorage-backed state                                                 */
/* ------------------------------------------------------------------------ */

/**
 * `useState` that mirrors its value into `localStorage`.
 *
 * The register ticket and the audit count sheet are hand-entered and easy to
 * lose — a stray refresh, a tab switch, or navigating to another page would
 * otherwise wipe a half-finished shift. This hook reads the cached value once
 * on mount (so the draft comes back) and writes it back on every change.
 *
 * Storage is treated as best-effort: if it is unavailable (private mode, quota
 * exceeded, storage disabled) the hook silently degrades to plain in-memory
 * state instead of throwing.
 *
 * @template T
 * @param {string} key localStorage key
 * @param {T} initialValue value used when nothing (valid) is cached
 * @returns {[T, import('react').Dispatch<import('react').SetStateAction<T>>]}
 */
export function usePersistentState(key, initialValue) {
  const [value, setValue] = useState(() => readCached(key, initialValue));

  useEffect(() => {
    writeCached(key, value);
  }, [key, value]);

  return [value, setValue];
}

/** Read + parse a cached value, falling back to `initialValue` on any problem. */
function readCached(key, initialValue) {
  if (typeof window === 'undefined') return initialValue;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw == null) return initialValue;
    const parsed = safeParse(raw, initialValue);
    // Guard against a stored `null` (or garbage that parsed to null) so callers
    // always get a usable object/array instead of a runtime crash.
    return parsed == null ? initialValue : parsed;
  } catch {
    return initialValue;
  }
}

/** Serialize + persist a value, swallowing storage failures. */
function writeCached(key, value) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage full or blocked — keep the value in memory only */
  }
}
